import { postDistributionEntries } from '../../domain/distributionCalc';
import { holdingsForState } from '../../domain/holdings';
import type { CallLine, CapitalCall, RegistryState } from '../../data/types';
import { money } from '../../lib/format';
import { newId } from '../../lib/random';
import type { RegistryAction } from '../actions';
import {
  actorName,
  addApproval,
  addCommunication,
  addDocuments,
  appendLedger,
  completeTasks,
  fundCode,
  holderName,
  today,
  withAudit,
} from '../helpers';
import { applicationPayment } from '../../domain/raise/raiseCalc';
import { recordReceipt } from './raiseHandlers';

type Handler<T extends RegistryAction['type']> = (
  state: RegistryState,
  action: Extract<RegistryAction, { type: T }>,
) => RegistryState;

export const saveDistribution: Handler<'DISTRIBUTION_SAVE'> = (state, action) => {
  const exists = state.distributions.some((item) => item.id === action.distribution.id);
  const distributions = exists
    ? state.distributions.map((item) =>
        item.id === action.distribution.id ? action.distribution : item,
      )
    : [action.distribution, ...state.distributions];
  return withAudit(
    { ...state, distributions },
    exists ? 'Distribution recalculated' : 'Distribution drafted',
    `${fundCode(state, action.distribution.fundId)} ${action.distribution.periodEnd}`,
    `${action.distribution.centsPerUnit} cpu · ${action.distribution.lines.length} holders`,
  );
};

export const submitDistribution: Handler<'DISTRIBUTION_SUBMIT'> = (state, action) => {
  const distribution = state.distributions.find((item) => item.id === action.id);
  if (!distribution) return state;
  let next: RegistryState = {
    ...state,
    distributions: state.distributions.map((item) =>
      item.id === action.id
        ? { ...item, status: 'PENDING_APPROVAL', createdBy: actorName(state) }
        : item,
    ),
  };
  const gross = distribution.lines.reduce((total, line) => total + line.gross, 0);
  next = addApproval(next, {
    kind: 'DISTRIBUTION',
    refId: distribution.id,
    fundId: distribution.fundId,
    title: `${fundCode(state, distribution.fundId)} distribution, period ending ${distribution.periodEnd}`,
    summary: `${distribution.centsPerUnit} cents per unit to ${distribution.lines.length} holders, payable ${distribution.paymentDate}.`,
    amount: gross,
  });
  return withAudit(
    next,
    'Distribution submitted for approval',
    fundCode(state, distribution.fundId),
    money(gross),
  );
};

export const payDistribution: Handler<'DISTRIBUTION_PAY'> = (state, action) => {
  const distribution = state.distributions.find((item) => item.id === action.id);
  const unitClass = state.classes.find((item) => item.id === distribution?.classId);
  if (!distribution || !unitClass || distribution.status !== 'APPROVED') return state;
  const paid = { ...distribution, status: 'PAID' as const, paymentDate: today };
  const posted = postDistributionEntries(paid, unitClass, 0, actorName(state));
  let next = appendLedger(
    {
      ...state,
      distributions: state.distributions.map((item) => (item.id === action.id ? paid : item)),
    },
    posted.map(({ seq: _seq, id: _id, postedAt: _postedAt, ...rest }) => rest),
  );
  next = addDocuments(
    next,
    paid.lines.map((line) => ({
      holderId: line.holderId,
      fundId: paid.fundId,
      kind: 'DISTRIBUTION_ADVICE' as const,
      refId: paid.id,
      name: `${fundCode(state, paid.fundId)} distribution advice, period ending ${paid.periodEnd}`,
      date: today,
      sizeKb: 84,
      visibleToInvestor: true,
    })),
  );
  return withAudit(
    next,
    'Distribution paid',
    `${fundCode(state, paid.fundId)} ${paid.periodEnd}`,
    `ABA file ${paid.abaFileName ?? ''} released; ${paid.lines.length} advices published`,
  );
};

export const createCall: Handler<'CALL_CREATE'> = (state, action) => {
  const holdings = holdingsForState(state, today).filter(
    (holding) => holding.classId === action.classId,
  );
  const existing = state.calls.filter((call) => call.fundId === action.fundId);
  const lines: CallLine[] = holdings
    .filter((holding) => (holding.commitment ?? 0) > 0)
    .map((holding, index) => ({
      holderId: holding.holderId,
      commitment: holding.commitment ?? 0,
      amount: Math.min(
        Math.round(((holding.commitment ?? 0) * action.callPct) / 100),
        holding.uncalled ?? 0,
      ),
      paid: 0,
      status: 'UNPAID' as const,
      crn: `78${String(10_000 + existing.length * 100 + index)}`,
    }))
    .filter((line) => line.amount > 0);
  const call: CapitalCall = {
    id: newId('cc'),
    fundId: action.fundId,
    classId: action.classId,
    number: existing.reduce((max, item) => Math.max(max, item.number), 0) + 1,
    callPct: action.callPct,
    purpose: action.purpose,
    issueDate: today,
    dueDate: action.dueDate,
    status: 'PENDING_APPROVAL',
    createdBy: actorName(state),
    lines,
  };
  const total = lines.reduce((sum, line) => sum + line.amount, 0);
  let next: RegistryState = { ...state, calls: [call, ...state.calls] };
  next = addApproval(next, {
    kind: 'CAPITAL_CALL',
    refId: call.id,
    fundId: call.fundId,
    title: `${fundCode(state, call.fundId)} Call ${call.number} (${call.callPct}%)`,
    summary: `${lines.length} notices, due ${call.dueDate}. ${call.purpose}`,
    amount: total,
  });
  return withAudit(
    next,
    'Capital call drafted',
    `${fundCode(state, call.fundId)} Call ${call.number}`,
    money(total),
  );
};

export const sendCallReminders: Handler<'CALL_SEND_REMINDERS'> = (state, action) => {
  const call = state.calls.find((item) => item.id === action.id);
  if (!call) return state;
  const outstanding = call.lines.filter((line) => line.status !== 'PAID').length;
  const reminded = addCommunication(state, {
    template: 'CALL_REMINDER',
    subject: `Reminder: ${fundCode(state, call.fundId)} Call ${call.number} is due ${call.dueDate}`,
    audience: `${fundCode(state, call.fundId)} holders yet to pay`,
    fundId: call.fundId,
    holderIds: call.lines.filter((line) => line.status !== 'PAID').map((line) => line.holderId),
    channels: ['EMAIL', 'PUSH'],
  });
  return withAudit(
    reminded,
    'Call reminders sent',
    `${fundCode(state, call.fundId)} Call ${call.number}`,
    `${outstanding} reminder emails and push notifications`,
  );
};

function applyCallPayment(
  state: RegistryState,
  callId: string,
  holderId: string,
  amount: number,
): RegistryState {
  const call = state.calls.find((item) => item.id === callId);
  if (!call) return state;
  const calls = state.calls.map((item) => {
    if (item.id !== callId) return item;
    return {
      ...item,
      lines: item.lines.map((line) => {
        if (line.holderId !== holderId) return line;
        const paid = Math.min(line.amount, line.paid + amount);
        return {
          ...line,
          paid,
          status: paid >= line.amount ? ('PAID' as const) : ('PARTIAL' as const),
        };
      }),
    };
  });
  let next = appendLedger({ ...state, calls }, [
    {
      fundId: call.fundId,
      classId: call.classId,
      holderId,
      type: 'CALL_PAYMENT',
      effectiveDate: today,
      units: amount,
      amount,
      unitPrice: 1,
      reference: `CALL ${call.number}`,
      eventId: call.id,
      memo: `Call ${call.number} payment received`,
      postedBy: actorName(state),
    },
  ]);
  const line = calls
    .find((item) => item.id === callId)
    ?.lines.find((item) => item.holderId === holderId);
  if (line?.status === 'PAID') {
    next = completeTasks(
      next,
      (task) => task.kind === 'PAY_CALL' && task.holderId === holderId && task.refId === callId,
    );
  }
  return next;
}

export const matchBankLine: Handler<'BANK_LINE_MATCH'> = (state, action) => {
  const line = state.bankLines.find((item) => item.id === action.lineId);
  if (!line) return state;
  let next: RegistryState = {
    ...state,
    bankLines: state.bankLines.map((item) =>
      item.id === action.lineId
        ? { ...item, status: 'MATCHED', matchedBy: actorName(state) }
        : item,
    ),
  };
  if (action.callId && action.holderId) {
    next = applyCallPayment(next, action.callId, action.holderId, line.amount);
    return withAudit(
      next,
      'Payment matched to call',
      holderName(state, action.holderId),
      money(line.amount, true),
    );
  }
  if (action.applicationId) {
    return recordReceipt(next, {
      applicationId: action.applicationId,
      amount: line.amount,
      date: line.date,
      reference: line.reference,
      source: 'BANK_FEED',
    });
  }
  return withAudit(next, 'Bank line matched', line.reference, money(line.amount, true));
};

export const ignoreBankLine: Handler<'BANK_LINE_IGNORE'> = (state, action) => {
  const line = state.bankLines.find((item) => item.id === action.lineId);
  if (!line) return state;
  return withAudit(
    {
      ...state,
      bankLines: state.bankLines.map((item) =>
        item.id === action.lineId ? { ...item, status: 'IGNORED' } : item,
      ),
    },
    'Bank line excluded',
    line.description,
    'Not a registry receipt',
  );
};

export const investorPayCall: Handler<'INVESTOR_PAY_CALL'> = (state, action) => {
  const call = state.calls.find((item) => item.id === action.callId);
  const line = call?.lines.find((item) => item.holderId === action.holderId);
  if (!call || !line) return state;
  const holder = state.holders.find((item) => item.id === action.holderId);
  const bankLine = {
    id: newId('bl'),
    fundId: call.fundId,
    date: today,
    amount: line.amount - line.paid,
    description: `BPAY ${(holder?.name ?? '').split(' ')[0].toUpperCase()}`,
    reference: line.crn,
    status: 'SUGGESTED' as const,
    suggestedHolderId: action.holderId,
    suggestedCallId: call.id,
  };
  return withAudit(
    { ...state, bankLines: [bankLine, ...state.bankLines] },
    'BPAY payment received',
    `${fundCode(state, call.fundId)} Call ${call.number}`,
    `${money(bankLine.amount)} from ${holder?.name ?? ''} (auto-suggested match)`,
    'Bank feed',
  );
};

export const investorPayApplication: Handler<'INVESTOR_PAY_APPLICATION'> = (state, action) => {
  const application = state.applications.find((item) => item.id === action.applicationId);
  if (!application) return state;
  const { outstanding } = applicationPayment(
    application,
    state.classes.find((item) => item.id === application.classId),
  );
  if (outstanding <= 0) return state;
  const bankLine = {
    id: newId('bl'),
    fundId: application.fundId,
    date: today,
    amount: outstanding,
    description: `EFT ${application.applicantName.split(' ')[0].toUpperCase()}`,
    reference: application.paymentInstruction?.crn ?? application.reference,
    status: 'SUGGESTED' as const,
    suggestedApplicationId: application.id,
  };
  return withAudit(
    { ...state, bankLines: [bankLine, ...state.bankLines] },
    'EFT received',
    application.reference,
    `${money(outstanding)} application monies (auto-suggested match)`,
    'Bank feed',
  );
};
